// ─────────────────────────────────────────────────────────────────────────────
// Crew — Privacy & data-rights surface (Agent D).
//
// A plain-language statement of what Crew collects and the UK-GDPR rights the user
// has, plus a working "erase my data" action (DELETE /api/me). Lives at the
// #/privacy hash route (wired in main.tsx) so it is reachable independently of
// Agent B's consumer UI and survives the App.tsx rewrite.
//
// Honesty is the contract: consent is granular + revocable, the pot only ever
// TRACKS money (never holds or moves it), and card-linking attaches an EXISTING
// card for attribution — Crew never issues a card.
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from 'react';
import { api, ApiError, isAuthed } from './lib/api';

export default function Privacy() {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  async function erase() {
    setBusy(true);
    setError(null);
    try {
      await api.eraseMe();
      setDone(true);
      setConfirming(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not complete erasure — please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto max-w-2xl p-6">
      <h1 className="text-3xl font-semibold tracking-tight text-ink-900">Privacy &amp; your data</h1>
      <p className="mt-2 text-ink-700">
        Crew is the demand layer for group experiences. We keep data collection lean
        and put you in control of it. Here is exactly what we hold and what you can do.
      </p>

      <Section title="What we collect">
        <ul className="list-disc space-y-1 pl-5 text-sm text-ink-700">
          <li><strong>Account:</strong> your email (for magic-link sign-in) and the display name you choose per group.</li>
          <li><strong>Coordination:</strong> the groups, goals, time polls and booking intents you create or join.</li>
          <li><strong>Analytics (consent-gated):</strong> product funnel events that help us improve Crew. Off unless you grant the analytics consent.</li>
          <li><strong>Card-linking (consent-gated):</strong> a link to your <em>existing</em> card via a third-party provider, for purchase attribution only. Crew never issues a card and never sees your card number.</li>
          <li><strong>Pot:</strong> a tracking-only ledger. Crew never holds or moves money.</li>
        </ul>
      </Section>

      <Section title="Your rights">
        <ul className="list-disc space-y-1 pl-5 text-sm text-ink-700">
          <li><strong>Consent is granular &amp; revocable</strong> — turn analytics, card-linking or marketing on or off at any time.</li>
          <li><strong>Access &amp; portability</strong> — see the data tied to your account.</li>
          <li><strong>Erasure</strong> — delete your personal data from Crew (below).</li>
        </ul>
      </Section>

      <Section title="Erase my data">
        {done ? (
          <p className="rounded-xl2 bg-amber-400/15 px-4 py-3 text-sm text-ink-900 ring-1 ring-amber-400/40">
            Your personal data has been erased and you have been signed out. Funnel
            analytics are kept only in anonymous, aggregate form with no link back to you.
          </p>
        ) : (
          <>
            <p className="text-sm text-ink-700">
              This permanently deletes your account, card link, consents and notification
              subscriptions, and signs you out. Behavioural analytics are anonymised — kept
              only as aggregate counts that can no longer be traced to you. This cannot be undone.
            </p>
            {error && (
              <p className="mt-3 rounded-xl2 bg-coral-500/10 px-4 py-3 text-sm text-coral-600 ring-1 ring-coral-500/20">
                {error}
              </p>
            )}
            {!isAuthed() ? (
              <p className="mt-3 text-sm text-ink-700/60">Sign in first to erase the data tied to your account.</p>
            ) : confirming ? (
              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  onClick={erase}
                  disabled={busy}
                  className="rounded-full bg-coral-600 px-5 py-2 text-sm font-medium text-white disabled:opacity-50"
                >
                  {busy ? 'Erasing…' : 'Yes, erase everything'}
                </button>
                <button
                  onClick={() => setConfirming(false)}
                  disabled={busy}
                  className="rounded-full px-5 py-2 text-sm font-medium text-ink-700 ring-1 ring-sand-200"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirming(true)}
                className="mt-4 rounded-full bg-coral-500 px-5 py-2 text-sm font-medium text-white"
              >
                Erase my data
              </button>
            )}
          </>
        )}
      </Section>

      <p className="mt-8 text-xs text-ink-700/50">
        Questions about your data? This is a vertical-slice build; production contact
        and full policy detail land before launch.
      </p>
    </main>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="mb-2 text-lg font-semibold text-ink-900">{title}</h2>
      {children}
    </section>
  );
}
